<template>
  <div>
    <div class="dialog-container">
      <!-- Header -->
      <div class="dialog-header">
        <div class="header-content">
          <div class="icon-wrapper" :style="{ background: getGradient }">
            <svg
              class="header-icon"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M3 21V7L12 3L21 7V21M3 21H21M5 21V9L12 6L19 9V21M9 21V14H15V21"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
          <div>
            <h2 class="dialog-title">Set Up Your {{ companyLabel }}</h2>
            <p class="dialog-subtitle">
              Set up your {{ companyLabel }}'s profile and branding
            </p>
          </div>
        </div>
      </div>

      <!-- Content -->
      <div class="dialog-content">
        <!-- Progress Indicator -->
        <div class="progress-steps">
          <div
            class="step"
            :class="{ active: currentStep >= 1, completed: currentStep > 1 }"
          >
            <div class="step-circle">
              <span v-if="currentStep > 1" class="check-mark">✓</span>
              <span v-else>1</span>
            </div>
            <span class="step-label">Basic Info</span>
          </div>
          <div class="step-line" :class="{ active: currentStep >= 2 }"></div>
          <div
            class="step"
            :class="{ active: currentStep >= 2, completed: currentStep > 2 }"
          >
            <div class="step-circle">
              <span v-if="currentStep > 2" class="check-mark">✓</span>
              <span v-else>2</span>
            </div>
            <span class="step-label">Location</span>
          </div>
          <div class="step-line" :class="{ active: currentStep >= 3 }"></div>
          <div class="step" :class="{ active: currentStep >= 3 }">
            <div class="step-circle">3</div>
            <span class="step-label">Theme</span>
          </div>
        </div>

        <!-- Step 1: Basic Information -->
        <div v-if="currentStep === 1" class="form-step">
          <div class="step-header">
            <h3 class="step-title">Basic Information</h3>
            <p class="step-description">
              Enter your {{ companyLabel }}'s name and contact details
            </p>
          </div>

          <div class="form-group">
            <label class="input-label">
              <svg class="label-icon" viewBox="0 0 20 20" fill="currentColor">
                <path
                  d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z"
                />
              </svg>
              {{ companyLabel }} Name
              <span class="required">*</span>
            </label>
            <input
              v-model="company.name"
              type="text"
              class="form-input"
              :placeholder="'e.g., Sample '+companyLabel"
              @input="validateStep1"
            />
            <p v-if="errors.name" class="error-text">
              {{ errors.name }}
            </p>
          </div>

          <div class="form-group">
            <label class="input-label">
              <svg class="label-icon" viewBox="0 0 20 20" fill="currentColor">
                <path
                  d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z"
                />
              </svg>
              Contact Number
              <span class="required">*</span>
            </label>
            <input
              v-model="company.primary_phone"
              type="tel"
              class="form-input"
              placeholder="e.g., +27 12 345 6789"
              @input="validateStep1"
            />
            <p v-if="errors.primary_phone" class="error-text">
              {{ errors.primary_phone }}
            </p>
          </div>

          <div class="form-group">
            <label class="input-label">
              <!-- <svg class="label-icon" viewBox="0 0 20 20" fill="currentColor">
                <path
                  d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z"
                />
              </svg> -->
              Industry
              <span class="required">*</span>
            </label>
            <v-select
              light
              v-model="company.industry"
              :items="companyIndustries"
              class="form-input"
              label="Industry"
              placeholder="e.g. Private Practice"
            ></v-select>
            <p v-if="errors.industry" class="error-text">
              {{ errors.industry }}
            </p>
          </div>
        </div>

        <!-- Step 2: Location -->
        <div v-if="currentStep === 2" class="form-step">
          <div class="step-header">
            <h3 class="step-title">Location Details</h3>
            <p class="step-description">Where is your company located?</p>
          </div>

          <div class="form-group">
            <label class="input-label">
              <svg class="label-icon" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fill-rule="evenodd"
                  d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                  clip-rule="evenodd"
                />
              </svg>
              Physical Address
              <span class="required">*</span>
            </label>
            <input
              v-model="company.primary_address"
              type="text"
              class="form-input"
              placeholder="e.g., 123 Main Street"
              @input="validateStep2"
            />
            <p v-if="errors.primary_address" class="error-text">
              {{ errors.primary_address }}
            </p>
          </div>

          <div class="form-group">
            <label class="input-label">
              <svg class="label-icon" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fill-rule="evenodd"
                  d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 110 2h-3a1 1 0 01-1-1v-2a1 1 0 00-1-1H9a1 1 0 00-1 1v2a1 1 0 01-1 1H4a1 1 0 110-2V4zm3 1h2v2H7V5zm2 4H7v2h2V9zm2-4h2v2h-2V5zm2 4h-2v2h2V9z"
                  clip-rule="evenodd"
                />
              </svg>
              City / Town / Suburb
              <span class="required">*</span>
            </label>
            <input
              v-model="company.city"
              type="text"
              class="form-input"
              placeholder="e.g., Johannesburg"
              @input="validateStep2"
            />
            <p v-if="errors.city" class="error-text">{{ errors.city }}</p>
          </div>
        </div>

        <!-- Step 3: Theme Selection -->
        <div v-if="currentStep === 3" class="form-step">
          <div class="step-header">
            <h3 class="step-title">Choose Your Brand Color</h3>
            
          </div>

          <div
            class="color-preview-card"
            :style="{ borderColor: company.primaryColor }"
          >
            <div class="preview-header" :style="{ background: getGradient }">
              <h4 class="preview-title">
                {{ company.name || "Your Company Name" }}
              </h4>
              <p class="preview-subtitle">{{ company.city || "City" }}</p>
            </div>
            <div class="preview-body">
             
            </div>
          </div>

          <div class="form-group">
            
            <div class="color-grid">
              <div
                v-for="color in predefinedColors"
                :key="color.value"
                class="color-option"
                :class="{ selected: company.primaryColor === color.value }"
                :style="{ backgroundColor: color.value }"
                @click="selectColor(color.value)"
              >
                <div
                  v-if="company.primaryColor === color.value"
                  class="color-check"
                >
                  <svg viewBox="0 0 20 20" fill="currentColor">
                    <path
                      fill-rule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clip-rule="evenodd"
                    />
                  </svg>
                </div>
                <span class="color-name">{{ color.name }}</span>
              </div>
            </div>
          </div>

          <div class="form-group">
            <label class="input-label">Or enter a custom color code</label>
            <div class="custom-color-input">
              <input
                v-model="company.primaryColor"
                type="text"
                class="form-input"
                placeholder="#0EA5E9"
                maxlength="7"
              />
              <input
                v-model="company.primaryColor"
                type="color"
                class="color-picker"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Actions -->
      <div class="dialog-actions">
        <button
          v-if="currentStep > 1"
          class="secondary-btn"
          @click="previousStep"
        >
          <svg class="btn-icon" viewBox="0 0 20 20" fill="currentColor">
            <path
              fill-rule="evenodd"
              d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
              clip-rule="evenodd"
            />
          </svg>
          Back
        </button>
        <button v-else class="secondary-btn" @click="cancelCreation">
          Cancel
        </button>

        <button
          v-if="currentStep < 3"
          class="primary-btn"
          :disabled="!isStepValid"
          @click="nextStep"
        >
          Continue
          <svg class="btn-icon" viewBox="0 0 20 20" fill="currentColor">
            <path
              fill-rule="evenodd"
              d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
              clip-rule="evenodd"
            />
          </svg>
        </button>
        <button v-else class="primary-btn" @click="createCompany">
          <svg class="btn-icon" viewBox="0 0 20 20" fill="currentColor">
            <path
              fill-rule="evenodd"
              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
              clip-rule="evenodd"
            />
          </svg>
          Submit {{ companyLabel }} Info
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "@/plugins/axios";
import helpersJs from "@/plugins/helpers";
import companiesJs from "@/plugins/companies"
import Loader from "@/components/Vuetify/Loader.vue";
export default {
  name: "CompanyCreationForm",
  props: {
    dialog: {
      default: false,
    },
  },
  components: { Loader },
  mounted() {
    this.companyIndustries = companiesJs.getCompanyIndustries()
    this.getAndSetUserEmail();
  },
  data() {
    return {
      companyIndustries: [],
      companyLabel:"Business",
      currentStep: 1,
      company: {
        name: "",
        domain: "127.0.0.1",
        primary_address: "",
        city: "",
        industry: "Legal Practice",
        primary_phone: "",
        emis: null,
        primaryColor: "#282F32",
        email: "",
      },
      errors: {
        name: "",
        primary_phone: "",
        primary_address: "",
        city: "",
        industry: "",
      },
      predefinedColors: [
        
        { name: "Default Legal", value: "#282F32" },
        { name: "Ocean Blue", value: "#0EA5E9" },
        // { name: "Forest Green", value: "#10B981" },
        // { name: "Royal Purple", value: "#8B5CF6" },
        { name: "Sunset Orange", value: "#F59E0B" },
        { name: "Cherry Red", value: "#EF4444" },
        { name: "Slate Gray", value: "#64748B" },
        { name: "Teal", value: "#14B8A6" },
        { name: "Indigo", value: "#6366F1" },
        // { name: "Rose", value: "#F43F5E" },
        { name: "Emerald", value: "#059669" },
        { name: "Amber", value: "#D97706" },
        { name: "Cyan", value: "#06B6D4" },
      ],
    };
  },
  computed: {
    isStepValid() {
      if (this.currentStep === 1) {
        return (
          this.company.name.trim() &&
          this.company.primary_phone.trim() && 
          this.company.industry.trim()
        );
      } else if (this.currentStep === 2) {
        return this.company.primary_address.trim() && this.company.city.trim();
      }
      return true;
    },
    getGradient() {
      const color = this.company.primaryColor;
      return (
        "linear-gradient(135deg, " +
        color +
        " 0%, " +
        this.adjustColor(color, -20) +
        " 100%)"
      );
    },
  },
  methods: {
    //done
    getAndSetUserEmail() {
      const user = JSON.parse(localStorage.getItem("login_progress"));
      if (!user || !user.email) {
        this.cancelCreation();
        return false;
      }
      this.company.email = user.email;
    },
    adjustColor(color, amount) {
      var usePound = false;
      if (color[0] === "#") {
        color = color.slice(1);
        usePound = true;
      }
      var num = parseInt(color, 16);
      var r = Math.max(0, Math.min(255, (num >> 16) + amount));
      var g = Math.max(0, Math.min(255, ((num >> 8) & 0x00ff) + amount));
      var b = Math.max(0, Math.min(255, (num & 0x0000ff) + amount));
      return (
        (usePound ? "#" : "") +
        (g | (b << 8) | (r << 16)).toString(16).padStart(6, "0")
      );
    },
    //done
    validateStep1() {
      if (!this.company.name.trim()) {
        this.errors.name = "Company name is required!";
      } else {
        this.errors.name = "";
      }
      if (!this.company.primary_phone.trim()) {
        this.errors.primary_phone = "Company primary contact number is required!";
      } else {
        this.errors.primary_phone = "";
      }
      if (!this.company.industry.trim()) {
        this.errors.industry = "Company industry is required!, to customise your experience!";
      } else {
        this.errors.industry = "";
      }
    },
    //done
    validateStep2() {
      if (!this.company.primary_address.trim()) {
        this.errors.primary_address = "Physical address is required";
      } else {
        this.errors.primary_address = "";
      }
      if (!this.company.city.trim()) {
        this.errors.city = "City/Town is required";
      } else {
        this.errors.city = "";
      }
    },
    //done
    nextStep() {
      if (this.isStepValid) {
        this.currentStep = this.currentStep + 1;
      }
    },
    //done
    previousStep() {
      this.currentStep = this.currentStep - 1;
    },
    //done
    selectColor(color) {
      this.company.primaryColor = color;
    },
    //done
    cancelCreation() {
      this.$emit("cancel-creation");
    },
    //done
    createCompany() {
      if(!this.company || !this.company.name || !this.company.email || !this.company.primary_phone){
        this.$toast.info("Company Name, Phone and Email fields are required!")
        return;
      }
      
      this.$store.commit("setLoader", true);
      axios
        .postData("/create-new-company", { company: this.company })
        .then((response) => {
          this.$store.commit("setLoader", false);
          if (response && response.status == "success") {
            helpersJs.initialiseOrUpdateCompanyDataInStorage(null,response.company);
            this.$emit("company-created");
          } else {
            this.$toast.error(response.message, { duration: 5000 });
          }
        })
        .catch((error) => {
          this.$store.commit("setLoader", false);
          console.error("Error creating company:", error);
        });
    },
  },
};
</script>

<style scoped>
* {
  box-sizing: border-box;
}

.dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
  overflow-y: auto;
}

.dialog-container {
  background: #ffffff;
  border-radius: 20px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  width: 100%;
  max-width: 600px;
  animation: slideUp 0.3s ease-out;
}

@keyframes slideUp {
  from {
    transform: translateY(30px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.dialog-header {
  padding: 32px 32px 24px;
  border-bottom: 2px solid #f3f4f6;
}

.header-content {
  display: flex;
  gap: 16px;
  align-items: flex-start;
}

.icon-wrapper {
  padding: 14px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.header-icon {
  width: 28px;
  height: 28px;
  color: #ffffff;
}

.dialog-title {
  font-size: 26px;
  font-weight: 700;
  color: #111827;
  margin: 0 0 6px 0;
  letter-spacing: -0.02em;
}

.dialog-subtitle {
  font-size: 15px;
  color: #6b7280;
  margin: 0;
}

.dialog-content {
  padding: 32px;
  min-height: 400px;
}

.progress-steps {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 40px;
  padding: 0 20px;
}

.step {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  flex: 0 0 auto;
}

.step-circle {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #e5e7eb;
  color: #9ca3af;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 16px;
  transition: all 0.3s;
}

.step.active .step-circle {
  background: linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%);
  color: #ffffff;
  box-shadow: 0 4px 12px rgba(14, 165, 233, 0.3);
  font-family: inherit;
}

.primary-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #0284c7 0%, #0369a1 100%);
  box-shadow: 0 6px 16px rgba(14, 165, 233, 0.4);
  transform: translateY(-1px);
}

.primary-btn:active:not(:disabled) {
  transform: translateY(0);
}

.primary-btn:disabled {
  background: #e5e7eb;
  color: #9ca3af;
  cursor: not-allowed;
  box-shadow: none;
}

.btn-icon {
  width: 18px;
  height: 18px;
}

@media (max-width: 640px) {
  .dialog-overlay {
    padding: 0;
    align-items: flex-start;
  }

  .dialog-container {
    border-radius: 20px 20px 0 0;
    max-height: 100vh;
    margin-top: 40px;
  }
  .dialog-header {
    padding: 24px 20px 20px;
  }

  .header-content {
    gap: 12px;
  }

  .icon-wrapper {
    padding: 12px;
  }

  .header-icon {
    width: 24px;
    height: 24px;
  }

  .dialog-title {
    font-size: 22px;
  }

  .dialog-subtitle {
    font-size: 14px;
  }

  .dialog-content {
    padding: 24px 20px;
    min-height: 350px;
  }

  .progress-steps {
    padding: 0 10px;
    margin-bottom: 32px;
  }

  .step-circle {
    width: 38px;
    height: 38px;
    font-size: 14px;
  }

  .step-label {
    font-size: 11px;
  }

  .step-line {
    margin: 0 6px;
  }

  .step-title {
    font-size: 18px;
  }

  .step-description {
    font-size: 13px;
  }

  .form-input {
    padding: 12px 14px;
    font-size: 14px;
  }

  .color-grid {
    grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
    gap: 10px;
  }

  .color-option {
    height: 70px;
    padding: 10px;
  }

  .color-name {
    font-size: 11px;
  }

  .dialog-actions {
    padding: 0 20px 24px;
    gap: 10px;
  }

  .secondary-btn,
  .primary-btn {
    padding: 12px 20px;
    font-size: 14px;
  }

  .custom-color-input {
    flex-direction: column;
  }

  .custom-color-input .form-input {
    width: 100%;
  }

  .color-picker {
    width: 100%;
  }
}

@media (max-width: 400px) {
  .progress-steps {
    padding: 0 5px;
  }

  .step-circle {
    width: 34px;
    height: 34px;
    font-size: 13px;
  }

  .step-label {
    font-size: 10px;
  }

  .step-line {
    margin: 0 4px;
  }

  .color-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

.step.completed .step-circle {
  background: #10b981;
  color: #ffffff;
}

.check-mark {
  font-size: 18px;
}

.step-label {
  font-size: 13px;
  font-weight: 600;
  color: #9ca3af;
  transition: color 0.3s;
}

.step.active .step-label {
  color: #0ea5e9;
}

.step-line {
  flex: 1;
  height: 3px;
  background: #e5e7eb;
  margin: 0 10px;
  transition: background 0.3s;
}

.step-line.active {
  background: #0ea5e9;
}

.form-step {
  animation: fadeIn 0.4s ease-in;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateX(10px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.step-header {
  margin-bottom: 28px;
}

.step-title {
  font-size: 20px;
  font-weight: 700;
  color: #111827;
  margin: 0 0 6px 0;
}

.step-description {
  font-size: 14px;
  color: #6b7280;
  margin: 0;
}

.form-group {
  margin-bottom: 24px;
}

.input-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 10px;
}

.label-icon {
  width: 18px;
  height: 18px;
  color: #6b7280;
}

.required {
  color: #ef4444;
  font-weight: 700;
}

.form-input {
  width: 100%;
  padding: 10px 10px;
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  font-size: 15px;
  color: #111827;
  transition: all 0.2s;
  font-family: inherit;
}

.form-input:focus {
  outline: none;
  border-color: #0ea5e9;
  box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.1);
}

.form-input::placeholder {
  color: #9ca3af;
}

.error-text {
  color: #ef4444;
  font-size: 13px;
  margin: 6px 0 0 0;
  font-weight: 500;
}

.color-preview-card {
  border: 3px solid #e5e7eb;
  border-radius: 14px;
  overflow: hidden;
  margin-bottom: 28px;
  transition: all 0.3s;
}

.preview-header {
  padding: 24px;
  color: #ffffff;
}

.preview-title {
  font-size: 20px;
  font-weight: 700;
  margin: 0 0 6px 0;
}

.preview-subtitle {
  font-size: 14px;
  margin: 0;
  opacity: 0.9;
}

.preview-body {
  padding: 20px 24px;
  background: #ffffff;
}

.preview-text {
  font-size: 14px;
  color: #6b7280;
  margin: 0;
}

.color-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  /* grid-template-columns: repeat(auto-fill, minmax(140px, 1fr)); */
  gap: 12px;
}

.color-option {
  height: 80px;
  border-radius: 10px;
  cursor: pointer;
  position: relative;
  transition: all 0.2s;
  display: flex;
  align-items: flex-end;
  padding: 12px;
  border: 3px solid transparent;
}

.color-option:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
}

.color-option.selected {
  border-color: #111827;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
}

.color-check {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 24px;
  height: 24px;
  background: #ffffff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.color-check svg {
  width: 16px;
  height: 16px;
  color: #10b981;
}

.color-name {
  font-size: 13px;
  font-weight: 600;
  color: #ffffff;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
}

.custom-color-input {
  display: flex;
  gap: 12px;
  align-items: center;
}

.custom-color-input .form-input {
  flex: 1;
}

.color-picker {
  width: 56px;
  height: 52px;
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s;
}

.color-picker:hover {
  border-color: #0ea5e9;
}

.dialog-actions {
  padding: 0 32px 32px;
  display: flex;
  gap: 12px;
}

.secondary-btn {
  flex: 1;
  background: transparent;
  color: #6b7280;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  padding: 14px 24px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-family: inherit;
}

.secondary-btn:hover {
  background-color: #f9fafb;
  border-color: #d1d5db;
  color: #4b5563;
}

.primary-btn {
  flex: 2;
  background: linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%);
  color: #ffffff;
  border: none;
  border-radius: 12px;
  padding: 14px 28px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  box-shadow: 0 4px 12px;
}
</style>
